import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'

import GuestListDataGrid from '../../components/dataGrid/GuestListDataGrid.jsx'
import GuestFormModal from '../../components/guestList/GuestFormModal.jsx'
import GuestImportModal from '../../components/guestList/GuestImportModal.jsx'
import DeleteGuestListPopup from '../../components/guestList/DeleteGuestListPopup.jsx'
import ButtonCustom from '../../components/atoms/button/ButtonCustom.jsx'
import styles from '../vendors-list/vendorsListPage.module.scss'
import {
	createGuestListGuest,
	deleteGuestListGuest,
	fetchCustomFields,
	fetchEventTags,
	fetchGuestList,
	fetchGuestTags,
	setIncludeArchived,
	updateGuestListGuest,
} from '../../features/guestListSlice.js'
import { useTutorial } from '../../context/TutorialContext'
import { useTutorialStatus } from '../../hooks/useTutorialStatus'

const ARCHIVE_TAG = 'Archive'
const DEFAULT_PAGE_SIZE = 50

export const KazooGuestsPage = () => {
	const dispatch = useDispatch()
	const { kazooId } = useParams()

	const {
		guests,
		total,
		page,
		pageSize,
		includeArchived,
		guestTags,
		eventTags,
		customFieldDefinitions,
		status,
	} = useSelector((state) => state.guestList)

	const [search, setSearch] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [importOpen, setImportOpen] = useState(false)
	const [editingGuest, setEditingGuest] = useState(null)
	const [deletingGuest, setDeletingGuest] = useState(null)
	const [listPage, setListPage] = useState(1)
	const [listPageSize, setListPageSize] = useState(DEFAULT_PAGE_SIZE)

	const { hasSeenTutorial, isLoading } = useTutorialStatus('wedding-guests')
	const { startTutorial, isTutorialActive } = useTutorial()

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
		return () => clearTimeout(t)
	}, [search])

	useEffect(() => {
		setListPage(1)
	}, [debouncedSearch, includeArchived])

	const reloadList = useCallback(() => {
		if (!kazooId) return
		dispatch(
			fetchGuestList({
				kazooId,
				includeArchived,
				page: listPage,
				pageSize: listPageSize,
				q: debouncedSearch,
			})
		)
	}, [
		dispatch,
		kazooId,
		includeArchived,
		listPage,
		listPageSize,
		debouncedSearch,
	])

	useEffect(() => {
		reloadList()
	}, [reloadList])

	useEffect(() => {
		if (!kazooId) return
		dispatch(fetchGuestTags(kazooId))
		dispatch(fetchEventTags(kazooId))
		dispatch(fetchCustomFields(kazooId))
	}, [dispatch, kazooId])

	useEffect(() => {
		if (!isLoading && !hasSeenTutorial && !isTutorialActive()) {
			const timer = setTimeout(() => startTutorial('wedding-guests'), 1000)
			return () => clearTimeout(timer)
		}
	}, [isLoading, hasSeenTutorial, isTutorialActive, startTutorial])

	const openAdd = () => {
		setEditingGuest(null)
		setFormOpen(true)
	}

	const openEdit = (guest) => {
		setEditingGuest(guest)
		setFormOpen(true)
	}

	const closeForm = () => {
		setFormOpen(false)
		setEditingGuest(null)
	}

	const handleSubmit = async (payload) => {
		if (editingGuest?._id) {
			const result = await dispatch(
				updateGuestListGuest({
					kazooId,
					guestId: editingGuest._id,
					...payload,
				})
			)
			if (updateGuestListGuest.fulfilled.match(result)) {
				closeForm()
				reloadList()
			}
			return
		}

		const result = await dispatch(
			createGuestListGuest({
				kazooId,
				...payload,
			})
		)
		if (createGuestListGuest.fulfilled.match(result)) {
			closeForm()
			reloadList()
			dispatch(fetchGuestTags(kazooId))
			dispatch(fetchEventTags(kazooId))
		}
	}

	const handleArchive = async (guest) => {
		const tags = Array.from(new Set([...(guest.guestTags || []), ARCHIVE_TAG]))
		await dispatch(
			updateGuestListGuest({
				kazooId,
				guestId: guest._id,
				guestTags: tags,
			})
		)
		reloadList()
		dispatch(fetchGuestTags(kazooId))
	}

	const handleConfirmDelete = async (guest) => {
		await dispatch(
			deleteGuestListGuest({
				kazooId,
				guestId: guest._id,
				confirm: true,
				acknowledgeArchiveRecommended: true,
			})
		)
		setDeletingGuest(null)
		reloadList()
	}

	const handleImported = () => {
		reloadList()
		dispatch(fetchGuestTags(kazooId))
		dispatch(fetchEventTags(kazooId))
	}

	const handlePaginationModelChange = (model) => {
		setListPage((model.page ?? 0) + 1)
		setListPageSize(model.pageSize ?? DEFAULT_PAGE_SIZE)
	}

	return (
		<div className={styles.pageWrap} data-cy="guest-list-page">
			<div className={styles.pageHeader}>
				<Typography component="h1">Guest List</Typography>
				<div className={styles.right}>
					<ButtonCustom
						variant="outlined"
						onClick={() => setImportOpen(true)}
						data-cy="import-guests-btn"
					>
						Import
					</ButtonCustom>
					<ButtonCustom
						variant="default"
						onClick={openAdd}
						data-cy="add-guest-btn"
					>
						+ Add Guest
					</ButtonCustom>
				</div>
			</div>

			<GuestListDataGrid
				guests={guests}
				total={total}
				page={page}
				pageSize={pageSize}
				kazooId={kazooId}
				eventTags={eventTags}
				guestTags={guestTags}
				customFieldDefinitions={customFieldDefinitions}
				includeArchived={includeArchived}
				onToggleIncludeArchived={(value) =>
					dispatch(setIncludeArchived(value))
				}
				searchValue={search}
				onSearchChange={setSearch}
				onEdit={openEdit}
				onDelete={setDeletingGuest}
				onArchive={handleArchive}
				loading={status === 'loading'}
				onPaginationModelChange={handlePaginationModelChange}
			/>

			<GuestFormModal
				open={formOpen}
				onClose={closeForm}
				guest={editingGuest}
				guestTags={guestTags}
				eventTags={eventTags}
				customFieldDefinitions={customFieldDefinitions}
				onSubmit={handleSubmit}
			/>

			<GuestImportModal
				open={importOpen}
				onClose={() => setImportOpen(false)}
				kazooId={kazooId}
				onImported={handleImported}
			/>

			<DeleteGuestListPopup
				open={Boolean(deletingGuest)}
				guest={deletingGuest}
				onClose={() => setDeletingGuest(null)}
				onConfirmDelete={handleConfirmDelete}
				onArchiveInstead={handleArchive}
			/>
		</div>
	)
}
